import { MouseEvent, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Card } from '../../../components/card/Card'
import { DropdownMenu } from '../../../components/dropdown/DropdownMenu'
import { translations } from '../../../configs/translations'
import { Vehicle } from '../../../models/Vehicle'
import { VehicleService } from '../../../services/VehicleService'
import { useStore } from '../../../store/store'
import { Invite } from '../invites/FreeroamInviteList'

interface FreeroamRouletteProps {
    selectedVehicle?: Vehicle | null
    setSelectedVehicle: (vehicle: Vehicle) => void
    invite: Invite
}

export const FreeroamRoulette = ({ selectedVehicle, setSelectedVehicle, invite }: FreeroamRouletteProps) => {
    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const rouletteRef = useRef<HTMLDivElement>(null)
    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [availableBrands, setAvailableBrands] = useState<string[]>([t.all.toUpperCase()])
    const [selectedBrand, setSelectedBrand] = useState<string | undefined>(undefined)
    const [availableRanks, setAvailableRanks] = useState<number[]>([0])
    const [selectedRank, setSelectedRank] = useState<number>(0)

    const fetchVehicles = async () => {
        await VehicleService.getUserVehicles({ brand: selectedBrand, rank: invite.rank, type: invite.carType })
            .then((response) => {
                setVehicles(response.vehicles)
                setAvailableBrands([t.all.toUpperCase(), ...response.availableBrands])
                setAvailableRanks([0, ...response.availableRanks])
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    useEffect(() => {
        fetchVehicles()
    }, [selectedVehicle])

    const handleMouseDown = (e: MouseEvent) => {
        setIsDragging(true)
        if (rouletteRef.current) {
            setStartX(e.pageX - rouletteRef.current.offsetLeft)
            setScrollLeft(rouletteRef.current.scrollLeft)
        }
    }

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        if (rouletteRef.current) {
            const x = e.pageX - rouletteRef.current.offsetLeft
            const walk = (x - startX) * 1.1
            rouletteRef.current.scrollLeft = scrollLeft - walk
        }
    }

    const handleMouseUp = () => {
        setIsDragging(false)
    }

    const handleMouseLeave = () => {
        setIsDragging(false)
    }

    const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false)
    const [isRankDropdownOpen, setIsRankDropdownOpen] = useState(false)

    useEffect(() => {
        fetchVehicles()
    }, [selectedBrand, selectedRank])

    return (
        <div className="roulette-main-container">
            <div className="filters">
                <DropdownMenu
                    title={selectedBrand || t.all}
                    minWidth={150}
                    children={
                        availableBrands.map((brand) =>
                            <div
                                className='dropdown-item'
                                key={brand}
                                onClick={() => {
                                    if (brand === t.all.toUpperCase()) {
                                        setSelectedBrand(undefined)
                                    } else {
                                        setSelectedBrand(brand)
                                    }
                                    setIsBrandDropdownOpen(false)
                                }}
                            >
                                {brand}
                            </div>
                        )
                    }
                    isOpen={isBrandDropdownOpen}
                    setIsOpen={setIsBrandDropdownOpen}
                />
                <DropdownMenu
                    title={selectedRank ? selectedRank.toString() : '0'}
                    minWidth={100}
                    children={
                        availableRanks.map((rank) =>
                            <div
                                className='dropdown-item'
                                key={rank}
                                onClick={() => {
                                    setSelectedRank(rank)
                                    setIsRankDropdownOpen(false)
                                }}
                            >
                                {rank}
                            </div>
                        )
                    }
                    isOpen={isRankDropdownOpen}
                    setIsOpen={setIsRankDropdownOpen}
                />
            </div>
            <div
                className="roulette"
                ref={rouletteRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                {vehicles.map((vehicle) => (
                    <Card
                        isSelected={selectedVehicle?.id === vehicle.id}
                        name={vehicle.name}
                        brand={vehicle.brand}
                        driveBias={vehicle.type as 'FWD' | 'RWD' | 'AWD'}
                        carRank={vehicle.rank}
                        onSelect={() => {
                            console.log('setting', vehicle)
                            setSelectedVehicle(vehicle)
                        }}
                    />
                ))}
            </div>
        </div>
    )
}
